/**
 * PACS Machine Service
 * GET/POST/PUT/DELETE /ris/api/pacs/machines
 */

import { API_ENDPOINTS } from '../config/api.config';
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  ApiError,
  extractApiErrorMessage,
} from '../utils/apiClient';
import {
  mapApiMachineToMachine,
  mapApiMachinesToMachines,
  applyConnectivityCheckToMachine,
} from '../utils/machineMapper';
import type {
  ConnectivityCheckResponse,
  Machine,
  MachineListFilters,
  MachineListResponse,
  MachineListResult,
  MachineResponse,
  MachineUpsertPayload,
  PacsMachineApi,
} from '../types/machine.types';

export interface MachineServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class MachineService {
  private extractErrorMessage(error: unknown): string {
    if (error instanceof ApiError) {
      if (error.response) {
        return extractApiErrorMessage(error.response, error.message);
      }
      return error.message;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unexpected error occurred';
  }

  private parseWrapper<T>(
    responseData: unknown,
    fallbackError: string
  ): { success: true; data: T; message?: string } | { success: false; error: string } {
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      const apiResponse = responseData as { success: boolean; data?: T; message?: string };
      if (apiResponse.success && apiResponse.data !== undefined) {
        return { success: true, data: apiResponse.data, message: apiResponse.message };
      }
      return { success: false, error: extractApiErrorMessage(responseData, fallbackError) };
    }
    return { success: true, data: responseData as T };
  }

  private buildMachineListQuery(filters: MachineListFilters): string {
    const queryParams = new URLSearchParams();
    if (filters.MachineName?.trim()) {
      queryParams.set('MachineName', filters.MachineName.trim());
    }
    if (filters.Modality?.trim()) {
      queryParams.set('Modality', filters.Modality.trim());
    }
    if (filters.Location?.trim()) {
      queryParams.set('Location', filters.Location.trim());
    }
    if (filters.AETitle?.trim()) {
      queryParams.set('AETitle', filters.AETitle.trim());
    }
    if (filters.active !== undefined && filters.active !== '') {
      queryParams.set('active', String(filters.active));
    }
    if (filters.offset !== undefined) {
      queryParams.set('offset', String(filters.offset));
    }
    if (filters.limit !== undefined) {
      queryParams.set('limit', String(filters.limit));
    }
    return queryParams.toString();
  }

  private extractRawMachineList(
    responseData: MachineListResponse | PacsMachineApi[]
  ): { success: true; rawList: PacsMachineApi[] } | { success: false; error: string } {
    if (Array.isArray(responseData)) {
      return { success: true, rawList: responseData };
    }
    if (responseData && typeof responseData === 'object') {
      const parsed = this.parseWrapper<PacsMachineApi[] | PacsMachineApi>(
        responseData,
        'Failed to fetch machines'
      );
      if (parsed.success === false) {
        return { success: false, error: parsed.error };
      }
      return {
        success: true,
        rawList: Array.isArray(parsed.data) ? parsed.data : [parsed.data],
      };
    }
    return { success: true, rawList: [] };
  }

  async getAllMachines(
    filters: MachineListFilters = {}
  ): Promise<MachineServiceResponse<MachineListResult>> {
    try {
      const queryString = this.buildMachineListQuery(filters);
      const url = queryString
        ? `${API_ENDPOINTS.PACS_MACHINES.LIST}?${queryString}`
        : API_ENDPOINTS.PACS_MACHINES.LIST;

      const response = await apiGet<MachineListResponse | PacsMachineApi[]>(url);
      const limit = filters.limit ?? 50;
      const offset = filters.offset ?? 0;

      const extracted = this.extractRawMachineList(response.data);
      if (extracted.success === false) {
        return { success: false, error: extracted.error, data: undefined };
      }

      const machines = mapApiMachinesToMachines(extracted.rawList);
      const hasMore = machines.length >= limit;

      return {
        success: true,
        data: { machines, offset, limit, hasMore },
        message:
          typeof response.data === 'object' && response.data && 'message' in response.data
            ? String((response.data as MachineListResponse).message)
            : undefined,
      };
    } catch (error) {
      console.error('Error fetching machines:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  async getMachineById(id: number | string): Promise<MachineServiceResponse<Machine>> {
    try {
      const response = await apiGet<MachineResponse | PacsMachineApi>(
        API_ENDPOINTS.PACS_MACHINES.GET_BY_ID(id)
      );
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<PacsMachineApi>(response.data, 'Failed to fetch machine');
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        return {
          success: true,
          data: mapApiMachineToMachine(parsed.data),
          message: parsed.message,
        };
      }
      return {
        success: true,
        data: mapApiMachineToMachine(response.data as PacsMachineApi),
      };
    } catch (error) {
      console.error('Error fetching machine:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }

  async createMachine(payload: MachineUpsertPayload): Promise<MachineServiceResponse<Machine>> {
    try {
      const response = await apiPost<MachineResponse | PacsMachineApi>(
        API_ENDPOINTS.PACS_MACHINES.CREATE,
        payload
      );
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<PacsMachineApi>(
          response.data,
          'Failed to register machine'
        );
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        return {
          success: true,
          data: mapApiMachineToMachine(parsed.data),
          message: parsed.message,
        };
      }
      return {
        success: true,
        data: mapApiMachineToMachine(response.data as PacsMachineApi),
      };
    } catch (error) {
      console.error('Error creating machine:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }

  async updateMachine(
    id: number | string,
    payload: MachineUpsertPayload
  ): Promise<MachineServiceResponse<Machine>> {
    try {
      const response = await apiPut<MachineResponse | PacsMachineApi>(
        API_ENDPOINTS.PACS_MACHINES.UPDATE(id),
        payload
      );
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<PacsMachineApi>(response.data, 'Failed to update machine');
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        return {
          success: true,
          data: mapApiMachineToMachine(parsed.data),
          message: parsed.message,
        };
      }
      return {
        success: true,
        data: mapApiMachineToMachine(response.data as PacsMachineApi),
      };
    } catch (error) {
      console.error('Error updating machine:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }

  async deactivateMachine(id: number | string): Promise<MachineServiceResponse<Machine>> {
    try {
      const response = await apiDelete<MachineResponse | PacsMachineApi>(
        API_ENDPOINTS.PACS_MACHINES.DELETE(id)
      );
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<PacsMachineApi>(
          response.data,
          'Failed to deactivate machine'
        );
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        return {
          success: true,
          data: mapApiMachineToMachine(parsed.data),
          message: parsed.message,
        };
      }
      if (response.data && typeof response.data === 'object' && 'pk' in response.data) {
        return {
          success: true,
          data: mapApiMachineToMachine(response.data as PacsMachineApi),
        };
      }
      return { success: true, data: undefined, message: 'Machine deactivated successfully' };
    } catch (error) {
      console.error('Error deactivating machine:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }

  async activateMachine(id: number | string): Promise<MachineServiceResponse<Machine>> {
    try {
      const response = await apiPut<MachineResponse | PacsMachineApi>(
        API_ENDPOINTS.PACS_MACHINES.ACTIVATE(id),
        {}
      );
      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<PacsMachineApi>(
          response.data,
          'Failed to activate machine'
        );
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        return {
          success: true,
          data: mapApiMachineToMachine(parsed.data),
          message: parsed.message,
        };
      }
      if (response.data && typeof response.data === 'object' && 'pk' in response.data) {
        return {
          success: true,
          data: mapApiMachineToMachine(response.data as PacsMachineApi),
        };
      }
      return { success: true, data: undefined, message: 'Machine activated successfully' };
    } catch (error) {
      console.error('Error activating machine:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }

  /** Update fields then apply activate/deactivate if status changed */
  async saveMachineWithStatus(
    id: number,
    payload: MachineUpsertPayload,
    wasActive: boolean,
    nowActive: boolean
  ): Promise<MachineServiceResponse<Machine>> {
    const updateResult = await this.updateMachine(id, payload);
    if (!updateResult.success) {
      return updateResult;
    }

    if (wasActive === nowActive) {
      return updateResult;
    }

    if (nowActive) {
      const activateResult = await this.activateMachine(id);
      if (!activateResult.success) {
        return activateResult;
      }
      return {
        success: true,
        data: activateResult.data ?? updateResult.data,
        message: activateResult.message,
      };
    }

    const deactivateResult = await this.deactivateMachine(id);
    if (!deactivateResult.success) {
      return deactivateResult;
    }
    return {
      success: true,
      data: deactivateResult.data ?? { ...updateResult.data!, active: false },
      message: deactivateResult.message,
    };
  }

  private async resolveMachineForConnectivityCheck(
    check: ConnectivityCheckResponse,
    existing?: Machine
  ): Promise<MachineServiceResponse<Machine>> {
    let baseMachine = existing;

    if (!baseMachine) {
      const machineResult = await this.getMachineById(check.machineId);
      if (!machineResult.success || !machineResult.data) {
        return {
          success: false,
          error: machineResult.error ?? 'Failed to fetch machine record',
        };
      }
      baseMachine = machineResult.data;
    }

    return {
      success: true,
      data: applyConnectivityCheckToMachine(baseMachine, check),
    };
  }

  async checkMachineConnectivity(
    id: number | string,
    existing?: Machine
  ): Promise<MachineServiceResponse<Machine>> {
    let baseMachine = existing;
    if (!baseMachine) {
      const machineResult = await this.getMachineById(id);
      if (!machineResult.success || !machineResult.data) {
        return {
          success: false,
          error: machineResult.error ?? 'Failed to fetch machine record',
        };
      }
      baseMachine = machineResult.data;
    }

    try {
      const response = await apiPost<{
        success: boolean;
        data?: ConnectivityCheckResponse;
        message?: string;
      }>(API_ENDPOINTS.PACS_MACHINES.CHECK_CONNECTIVITY(id), {});

      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<ConnectivityCheckResponse>(
          response.data,
          'Failed to check connectivity'
        );
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        const machineResult = await this.resolveMachineForConnectivityCheck(
          parsed.data,
          baseMachine
        );
        if (!machineResult.success) {
          return machineResult;
        }
        return {
          success: true,
          data: machineResult.data,
          message: parsed.message ?? response.data.message,
        };
      }

      const check = response.data as ConnectivityCheckResponse;
      const machineResult = await this.resolveMachineForConnectivityCheck(check, baseMachine);
      if (!machineResult.success) {
        return machineResult;
      }
      return { success: true, data: machineResult.data };
    } catch (error) {
      console.error('Error checking machine connectivity:', error);
      const message = this.extractErrorMessage(error);
      // Surface known configuration failures as a success payload so the UI can show Configuration error.
      if (/configur/i.test(message) || /misconfigur/i.test(message)) {
        return {
          success: true,
          data: {
            ...baseMachine,
            connectivityStatus: 'ConfigurationError',
            failureReason: message,
            responseTime: null,
          },
        };
      }
      return { success: false, error: message };
    }
  }

  async checkConnectivityByAeTitle(
    aeTitle: string,
    existing?: Machine
  ): Promise<MachineServiceResponse<Machine>> {
    const trimmed = aeTitle.trim();
    if (!trimmed) {
      return { success: false, error: 'AE title is required' };
    }

    try {
      const response = await apiPost<{
        success: boolean;
        data?: ConnectivityCheckResponse;
        message?: string;
      }>(API_ENDPOINTS.PACS_CONNECTIVITY.CHECK_BY_AE_TITLE(trimmed), {});

      if (response.data && typeof response.data === 'object' && 'success' in response.data) {
        const parsed = this.parseWrapper<ConnectivityCheckResponse>(
          response.data,
          'Failed to check connectivity'
        );
        if (parsed.success === false) {
          return { success: false, error: parsed.error };
        }
        const machineResult = await this.resolveMachineForConnectivityCheck(parsed.data, existing);
        if (!machineResult.success) {
          return machineResult;
        }
        return {
          success: true,
          data: machineResult.data,
          message: parsed.message ?? response.data.message,
        };
      }

      const check = response.data as ConnectivityCheckResponse;
      const machineResult = await this.resolveMachineForConnectivityCheck(check, existing);
      if (!machineResult.success) {
        return machineResult;
      }
      return { success: true, data: machineResult.data };
    } catch (error) {
      console.error('Error checking connectivity by AE title:', error);
      return { success: false, error: this.extractErrorMessage(error) };
    }
  }
}

const machineService = new MachineService();
export default machineService;
